import { onAuthStateChanged } from 'firebase/auth';
import { 
    App, 
    AuthenticatedUser, 
    Data, 
    Nullable, 
    Query, 
    Queryable, 
    Therapist, 
    HealthcareProviderInfo,
    UI, 
    User, 
    DashboardRole,
    UserType,
} from '../interfaces/Frameworks';
import { auth } from '../firebase';
import { 
    analyticsLogEvent, 
    analyticsSetUser,
    initAnalytics,
} from '../Analytics';
import { newQueryable } from '../Queryable';
import { newData } from '../Data';
import {
    getDashboardRole,
    getUserTypeForDashboardRole,
    isDashboardProviderHydrated,
} from '../DashboardAccess';
import { isPublicSaasPath } from '../saas/publicRoutes';

class AppImpl implements App {
    private ui?: UI;
    private authUnsubscribe?: () => void;
    private authenticatedUser: AuthenticatedUser = null;
    private userType: UserType = 'care_recipient';
    private dashboardRole: DashboardRole = 'unauthenticated';
    private userQueryable!: Queryable<User>;
    private data!: Data;
    private pendingPasswordChange: boolean = false;
    private currentTherapist: Therapist | null = null;
    private healthcareProviderInfo: HealthcareProviderInfo | null = null;
    private dashboardInitialized: boolean = false;

    constructor(options: { name: string }) {
        console.log(`APP: constructor(name: ${options.name})`);
    }

    start() {
        console.log('APP: start()');

        // Start analytics before UI observers can report the initial dashboard view.
        void initAnalytics();

        // initialize Data
        this.data = newData({app: this});
        this.data.start();

        // initialize UI
        if (this.ui) {
            this.ui.start();
        }

        // observe current therapist
        this.observeCurrentTherapist();

        // observe the provider projection needed to render features atomically
        this.observeHealthcareProviderInfo();

        // observe authentication state
        this.observeAuthenticationState();
    }

    private getUserEmail(user: AuthenticatedUser | null): Nullable<string> {
        if (!user) return null;
        if (user.email) {
            return user.email;
        }
        for (const info of user.providerData) {
            if (info?.email) {
                return info.email;
            }
        }
        return null;
    }

    private observeAuthenticationState() {
        // whenever a user logs in or logs out...
        this.authUnsubscribe = onAuthStateChanged(auth, (currentUser: AuthenticatedUser) => {
            console.log('APP: onAuthStateChanged(user: ', currentUser, ')');
            const previousUserId = this.authenticatedUser?.uid || '';
            const currentUserId = currentUser?.uid || '';

            // Attribute completed auth transitions before notifying dashboard observers.
            if (previousUserId && !currentUserId) {
                analyticsLogEvent('Logout');
            }
            analyticsSetUser(currentUserId || null);
            if (currentUserId && previousUserId !== currentUserId) {
                analyticsLogEvent('Login');
            }
            this.authenticatedUser = currentUser;

            if (previousUserId !== currentUserId) {
                // Fail closed while the newly authenticated user's Therapist record loads.
                this.currentTherapist = null;
                this.healthcareProviderInfo = null;
                this.dashboardInitialized = false;
                this.dashboardRole = 'unauthenticated';
                this.userType = 'care_recipient';
                this.userQueryable?.setState({
                    authenticatedUser: this.authenticatedUser,
                    type: this.userType,
                    dashboardRole: this.dashboardRole,
                });
            }

            if (currentUser?.email == null) {
                const userEmail = this.getUserEmail(currentUser);
                if (userEmail) {
                    (currentUser as any).email = userEmail as string;
                }
            }

            const userEmail = this.getUserEmail(currentUser);
            if (isPublicSaasPath(window.location.pathname)) {
                // Signup and checkout keep verified owners signed in before their Therapist is provisioned.
                this.dashboardRole = 'unauthenticated';
                this.userType = 'care_recipient';
                this.userQueryable?.setState({
                    authenticatedUser: this.authenticatedUser,
                    type: this.userType,
                    dashboardRole: this.dashboardRole,
                });
                return;
            }
            if (currentUser) {
                this.ui?.setState('loading');
            }
            this.data.fetchTherapistForUser(currentUser?.uid || '', userEmail || '');

            if (!currentUser) {
                // User has logged out or is not logged in
                this.pendingPasswordChange = false;
                this.currentTherapist = null;
                this.healthcareProviderInfo = null;
                this.dashboardInitialized = false;
                this.ui?.setState('login');
            }
        });
    }

    private observeCurrentTherapist() {
        this.data.queryCurrentTherapist().observe({
            changed: (therapist: Therapist) => {
                console.log('APP: queryCurrentTherapist().observe()', therapist);
                const authenticatedEmail = (this.getUserEmail(this.authenticatedUser) || '').trim().toLowerCase();
                const therapistEmail = (therapist.email || '').trim().toLowerCase();
                const isVerifiedMicrosoftUser = this.authenticatedUser?.emailVerified === true &&
                    this.authenticatedUser.providerData.some((provider) => provider?.providerId === 'microsoft.com');
                const therapistMatchesUser = !!this.authenticatedUser && !!therapist.id && (
                    therapist.id === this.authenticatedUser.uid ||
                    (isVerifiedMicrosoftUser && !!authenticatedEmail && authenticatedEmail === therapistEmail)
                );
                this.currentTherapist = therapistMatchesUser ? therapist : null;
                const previousDashboardRole = this.dashboardRole;
                this.dashboardRole = therapistMatchesUser ? getDashboardRole(therapist) : 'unauthenticated';
                this.userType = therapistMatchesUser ? getUserTypeForDashboardRole(this.dashboardRole) : 'care_recipient';
                this.userQueryable?.setState({ 
                    authenticatedUser: this.authenticatedUser, 
                    type: this.userType,
                    dashboardRole: this.dashboardRole,
                });

                if (this.authenticatedUser && previousDashboardRole !== this.dashboardRole) {
                    // The server refreshes claims on role edits; force this browser onto them.
                    void this.authenticatedUser.getIdToken(true).catch((error) =>
                        console.error('APP: Could not refresh role claims:', error)
                    );
                }
                
                // Check if user needs to change their password
                if (therapistMatchesUser && therapist.mustChangePassword === true) {
                    console.log('APP: User must change password, redirecting to password change screen');
                    this.pendingPasswordChange = true;
                    this.ui?.setState('changePassword');
                } else if (therapistMatchesUser) {
                    this.pendingPasswordChange = false;
                    this.maybeOpenDashboard();
                }
            }
        });
    }

    private observeHealthcareProviderInfo() {
        this.data.queryHealthcareProviderInfo().observe({
            changed: (healthcareProviderInfo: HealthcareProviderInfo) => {
                this.healthcareProviderInfo = healthcareProviderInfo;
                this.maybeOpenDashboard();
            },
        });
    }

    /** Enter once the home provider and its feature projection match the authenticated Therapist. */
    private maybeOpenDashboard() {
        if (this.dashboardInitialized || this.pendingPasswordChange ||
            isPublicSaasPath(window.location.pathname) || !this.currentTherapist) {
            return;
        }
        const ready = isDashboardProviderHydrated(
            this.authenticatedUser,
            this.currentTherapist,
            this.currentTherapist.healthcareProviderId,
            this.healthcareProviderInfo
        );
        if (!ready) {
            this.ui?.setState('loading');
            return;
        }
        this.dashboardInitialized = true;
        this.ui?.setState('dashboard');
    }

    stop() {
        console.log('APP: stop()');
        if (this.authUnsubscribe) {
            this.authUnsubscribe();
        }
    }

    registerUI(ui: UI) {
        console.log('APP: registerUI(ui)');
        this.ui = ui;
    }

    queryUser(): Query<User> {
        if (this.userQueryable == undefined) {
            this.userQueryable = newQueryable({
                authenticatedUser: this.authenticatedUser,
                type: this.userType,
                dashboardRole: this.dashboardRole,
            }, 'USER');
        }
        return this.userQueryable.query();
    }

    getData(): Data {
        return this.data;
    }
}

export function newApp(options: {name: string }): App {
    return new AppImpl(options);
}
